import { useEffect, useState } from 'react';
import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import { DynamicIcon } from '~/elements';
import { useColorValue } from '~/hooks';

interface DeviceData {
  os: string;
  browser: string;
  language: string;
  resolution: string;
  timezone: string;
  cookies: string;
}

/**
 * Navegadores modernos congelam parte do user-agent (o Chrome reporta
 * "Mac OS X 10_15_7" em qualquer macOS), entao isto e uma aproximacao
 * declarada pelo proprio navegador, nao uma deteccao confiavel.
 */
function detectOS(ua: string): string {
  const win = ua.match(/Windows NT ([\d.]+)/);
  if (win !== null) {
    const versions: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    };
    return `Windows ${versions[win[1]] ?? win[1]}`;
  }
  const mac = ua.match(/Mac OS X ([\d_]+)/);
  if (mac !== null) return `macOS ${mac[1].replace(/_/g, '.')}`;
  const android = ua.match(/Android ([\d.]+)/);
  if (android !== null) return `Android ${android[1]}`;
  const ios = ua.match(/OS ([\d_]+) like Mac OS X/);
  if (ios !== null) return `iOS ${ios[1].replace(/_/g, '.')}`;
  if (/CrOS/.test(ua)) return 'ChromeOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Desconhecido';
}

function detectBrowser(ua: string): string {
  // A ordem importa: Edge e Opera tambem contem "Chrome" no user-agent.
  const tests: [RegExp, string][] = [
    [/Edg\/([\d.]+)/, 'Edge'],
    [/OPR\/([\d.]+)/, 'Opera'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Version\/([\d.]+).*Safari/, 'Safari'],
  ];
  for (const [re, name] of tests) {
    const m = ua.match(re);
    if (m !== null) return `${name} ${m[1].split('.')[0]}`;
  }
  return 'Desconhecido';
}

export const DeviceInfo = (): JSX.Element | null => {
  const [data, setData] = useState<DeviceData | null>(null);
  const [clock, setClock] = useState<string>('');

  const labelColor = useColorValue('gray.500', 'whiteAlpha.600');
  const valueColor = useColorValue('gray.800', 'whiteAlpha.900');
  const borderColor = useColorValue('blackAlpha.200', 'whiteAlpha.300');
  const noteColor = useColorValue('gray.400', 'whiteAlpha.500');

  // navigator/screen so existem no browser; o Next renderiza no servidor antes.
  useEffect(() => {
    const ua = navigator.userAgent;
    setData({
      os: detectOS(ua),
      browser: detectBrowser(ua),
      language: navigator.language || '--',
      resolution: `${window.screen.width} x ${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '--',
      cookies: navigator.cookieEnabled ? 'Habilitados' : 'Desabilitados',
    });
  }, []);

  useEffect(() => {
    const tick = (): void => setClock(new Date().toLocaleString('pt-BR'));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (data === null) return null;

  const rows: [string, string][] = [
    ['Sistema', data.os],
    ['Navegador', data.browser],
    ['Idioma', data.language],
    ['Resolucao', data.resolution],
    ['Hora local', clock],
    ['Fuso', data.timezone],
    ['Cookies', data.cookies],
  ];

  return (
    <Box w="100%" borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4}>
      <Flex align="center" gap={2} mb={3}>
        <Flex
          align="center"
          justify="center"
          boxSize="28px"
          borderRadius="md"
          bgGradient="linear(to-br, primary.400, secondary.400)"
          color="white"
          flexShrink={0}
        >
          <DynamicIcon icon={{ fa: 'FaMicrochip' }} boxSize="14px" />
        </Flex>
        <Text fontSize="sm" fontWeight="bold">
          Navegador e dispositivo
        </Text>
      </Flex>

      <Divider borderColor={borderColor} />

      <VStack align="stretch" spacing={1.5} py={3}>
        {rows.map(([label, value]) => (
          <Flex key={label} justify="space-between" align="baseline" gap={3}>
            <Text fontSize="xs" color={labelColor} flexShrink={0}>
              {label}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color={valueColor}
              textAlign="right"
              wordBreak="break-word"
            >
              {value}
            </Text>
          </Flex>
        ))}
      </VStack>

      <Divider borderColor={borderColor} />

      <Text fontSize="10px" color={noteColor} mt={3} lineHeight="short">
        Lido do seu navegador via JavaScript. Nada e enviado para nenhum servidor.
      </Text>
    </Box>
  );
};
